/**
 * useUrlHashState — sync a single string value with the URL hash fragment.
 *
 * Stores `key=value` (URL-encoded) within `location.hash` so that a demo's
 * active level, panel, or filter persists across reloads and is shareable
 * as a deep link. The hook is theme-agnostic and free of side effects beyond
 * reading and writing `location.hash`.
 *
 * Multiple keys can coexist in the same hash (`#a=1&b=2`); each instance of
 * the hook owns exactly one key. The hash parse / serialize / commit
 * primitives are shared with `useUrlHashRoute` via `./urlHashParams.js`.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useCallback, useEffect, useState } from "react";
import { commitHashKey, readHashKey } from "./urlHashParams.js";

/**
 * Bind a single state value to `location.hash[key]`.
 *
 * @param key   Hash parameter name (must be a valid URL parameter key).
 * @param fallback Value used when the hash is empty or invalid.
 * @param isValid  Optional guard rejecting unknown hash values.
 * @returns `[value, setValue]` — `setValue(null)` clears the hash entry.
 */
export function useUrlHashState<T extends string>(
  key: string,
  fallback: T,
  isValid?: (value: string) => value is T,
): readonly [T, (value: T | null) => void] {
  const read = useCallback((): T => {
    const raw = readHashKey(key);
    if (raw === null) return fallback;
    if (isValid && !isValid(raw)) return fallback;
    return raw as T;
  }, [key, fallback, isValid]);

  const [value, setValue] = useState<T>(read);

  useEffect(() => {
    const handler = (): void => {
      setValue(read());
    };
    window.addEventListener("hashchange", handler);
    return (): void => {
      window.removeEventListener("hashchange", handler);
    };
  }, [read]);

  const update = useCallback(
    (next: T | null): void => {
      // Replace, not push: in-demo state should not flood the Back stack.
      commitHashKey(key, next, "replace");
      setValue(next ?? fallback);
    },
    [key, fallback],
  );

  return [value, update] as const;
}
